<script>
  import { onDestroy, tick } from 'svelte';
  import Portal from 'svelte-portal';
  import { LoaderCircle, RefreshCw, Users } from 'lucide-svelte';
  import { fade, scale } from 'svelte/transition';
  import { quintOut } from 'svelte/easing';
  import { loadMessageReactorsWS, myId } from '$lib/websocket-client.js';
  import { avatarContentUrl } from '$lib/avatar';

  const VIEWPORT_PADDING = 10;
  const POPOVER_GAP = 7;
  const REACTION_BURST_SIZE_SCALE = 4;

  export let messageId;
  export let reactionSummary = [];
  export let fromMe = false;
  export let interactionMode = 'desktop';
  export let onOpenChange = () => {};
  export let summary = 3;
  export let effectMode = 'random';
  export let effectOrigin = 'center';
  export let transitionSpec = 'opacity 180ms cubic-bezier(0.16, 1, 0.3, 1) 0ms';
  export let effectCount = 12;

  let triggerEl;
  let popoverEl;
  let open = false;
  let loading = false;
  let error = '';
  let details = null;
  let loadedSummarySignature = '';
  let selectedReaction = 'all';
  let popupPos = { x: 0, y: 0 };
  let listenersBound = false;
  let burstSeq = 0;
  let burstParticles = [];
  let burstTimers = new Map();
  let lastEffectKey = '';
  let showEffectSettings = false;
  const BURST_ICONS = ['✨', '💫', '🌟', '💥', '🫧', '✦'];
  const BURST_COLORS = ['#ffffff', '#c4b5fd', '#fde68a', '#93c5fd', '#86efac', '#fca5a5'];

  $: total = reactionSummary.reduce((sum, item) => sum + Number(item?.[1] || 0), 0);
  $: summarySignature = JSON.stringify(reactionSummary);
  $: previewReactions = reactionSummary.slice(0, summary).map((item) => item[0]);
  $: filters = details?.summary?.length
    ? details.summary
    : reactionSummary.map(([reaction, count]) => ({ reaction, count, anonymousCount: 0 }));
  $: filteredRows = (details?.rows || []).filter(
    (row) => selectedReaction === 'all' || row.reaction === selectedReaction
  );
  $: anonymousForFilter =
    selectedReaction === 'all'
      ? Number(details?.anonymousCount || 0) + Number(details?.hiddenCount || 0)
      : (() => {
          const item = details?.summary?.find((entry) => entry.reaction === selectedReaction);
          const knownVisible = filteredRows.length;
          return Math.max(0, Number(item?.count || 0) - knownVisible);
        })();
  $: effectKey = `${open ? 1 : 0}:${selectedReaction}:${summarySignature}`;
  $: if (open && effectMode !== 'off' && effectKey !== lastEffectKey) {
    lastEffectKey = effectKey;
    spawnBurst();
  }
  $: if (open && details && !loading && loadedSummarySignature && loadedSummarySignature !== summarySignature) {
    void loadDetails(true);
  }

  function getTransitionValue() {
    if (typeof transitionSpec === 'string' && transitionSpec.trim()) {
      return transitionSpec.trim();
    }

    if (transitionSpec && typeof transitionSpec === 'object') {
      const property = String(transitionSpec.property || 'transform, opacity').trim();
      const duration = String(transitionSpec.duration || '180ms').trim();
      const timingFunction = String(
        transitionSpec.timingFunction || transitionSpec.easing || 'cubic-bezier(0.16, 1, 0.3, 1)'
      ).trim();
      const delay = String(transitionSpec.delay || '0ms').trim();
      return `${property} ${duration} ${timingFunction} ${delay}`.trim();
    }

    return 'opacity 180ms cubic-bezier(0.16, 1, 0.3, 1) 0ms';
  }

  function clearBurstTimers() {
    for (const timer of burstTimers.values()) clearTimeout(timer);
    burstTimers.clear();
  }

  function spawnBurst() {
    clearBurstTimers();

    if (effectMode === 'off') {
      burstParticles = [];
      return;
    }

    const count = Math.max(4, Math.min(24, Number(effectCount) || 12));
    const indices = Array.from({ length: count }, (_, index) => index);
    const ordered = effectMode === 'sequential' ? indices : indices.sort(() => Math.random() - 0.5);
    const origin = effectOrigin === 'center' ? { x: 50, y: 50 } : fromMe ? { x: 78, y: 66 } : { x: 22, y: 66 };

    burstParticles = ordered.map((index, slot) => {
      const angle = -Math.PI / 2 + Math.PI * 1.5 * (slot / Math.max(1, count - 1)) + (Math.random() * 0.18 - 0.09);
      const distance = 42 + Math.random() * 52;
      const lift = 16 + Math.random() * 24;
      const sway = Math.random() * 14 - 7;
      const isEmoji = index % 3 === 0;
      const delay = effectMode === 'sequential' ? slot * 28 : Math.round(Math.random() * 90);

      return {
        id: `${Date.now()}-${burstSeq++}-${index}`,
        x: origin.x,
        y: origin.y,
        tx: Math.cos(angle) * distance + sway,
        ty: Math.sin(angle) * distance - lift,
        delay,
        size: (isEmoji ? 15 + Math.random() * 7 : 5 + Math.random() * 4) * REACTION_BURST_SIZE_SCALE,
        icon: isEmoji
          ? Math.random() > 0.5
            ? BURST_ICONS[index % BURST_ICONS.length]
            : BURST_ICONS[(index + 2) % BURST_ICONS.length]
          : null,
        color: BURST_COLORS[index % BURST_COLORS.length]
      };
    });

    const timer = setTimeout(() => {
      burstParticles = [];
      clearBurstTimers();
    }, 920);
    burstTimers.set('burst', timer);
  }

  async function syncPosition() {
    if (!open || !triggerEl) return;
    await tick();

    const triggerRect = triggerEl.getBoundingClientRect();
    const width = popoverEl?.offsetWidth || Math.min(292, window.innerWidth - VIEWPORT_PADDING * 2);
    const height = popoverEl?.offsetHeight || 260;
    const rawX = fromMe ? triggerRect.right - width : triggerRect.left;
    const x = Math.max(VIEWPORT_PADDING, Math.min(rawX, window.innerWidth - width - VIEWPORT_PADDING));
    const above = triggerRect.top - height - POPOVER_GAP;
    const below = triggerRect.bottom + POPOVER_GAP;
    const y = above >= VIEWPORT_PADDING ? above : Math.min(below, window.innerHeight - height - VIEWPORT_PADDING);

    popupPos = { x, y: Math.max(VIEWPORT_PADDING, y) };
  }

  function bindListeners() {
    if (listenersBound) return;
    window.addEventListener('pointerdown', handleOutsidePointer, true);
    window.addEventListener('keydown', handleKeydown);
    window.addEventListener('resize', handleViewportChange);
    window.addEventListener('scroll', handleViewportChange, true);
    listenersBound = true;
  }

  function unbindListeners() {
    if (!listenersBound) return;
    window.removeEventListener('pointerdown', handleOutsidePointer, true);
    window.removeEventListener('keydown', handleKeydown);
    window.removeEventListener('resize', handleViewportChange);
    window.removeEventListener('scroll', handleViewportChange, true);
    listenersBound = false;
  }

  async function loadDetails(force = false) {
    loading = true;
    error = '';

    try {
      details = await loadMessageReactorsWS(messageId, { force });
      loadedSummarySignature = summarySignature;
      if (selectedReaction !== 'all' && !details.summary.some((item) => item.reaction === selectedReaction)) {
        selectedReaction = 'all';
      }
    } catch (loadError) {
      error = loadError?.message || 'Không thể tải danh sách cảm xúc.';
    } finally {
      loading = false;
      void syncPosition();
    }
  }

  function openViewer(event) {
    event.preventDefault();
    event.stopPropagation();

    if (open) {
      closeViewer();
      return;
    }

    open = true;
    selectedReaction = 'all';
    onOpenChange(true);
    bindListeners();
    void syncPosition();
    void loadDetails();
  }

  function closeViewer() {
    if (!open) return;
    open = false;
    onOpenChange(false);
    unbindListeners();
    burstParticles = [];
    clearBurstTimers();
  }

  function handleOutsidePointer(event) {
    if (triggerEl?.contains(event.target) || popoverEl?.contains(event.target)) return;
    closeViewer();
  }

  function handleKeydown(event) {
    if (event.key === 'Escape') closeViewer();
  }

  function handleViewportChange() {
    if (open) void syncPosition();
  }

  function setEffectMode(mode) {
    effectMode = mode;
    lastEffectKey = '';
    spawnBurst();
  }

  function setEffectOrigin(origin) {
    effectOrigin = origin;
    lastEffectKey = '';
    spawnBurst();
  }

  function getInitial(name) {
    return (
      String(name || '?')
        .trim()
        .charAt(0)
        .toUpperCase() || '?'
    );
  }

  onDestroy(() => {
    unbindListeners();
    onOpenChange(false);
    clearBurstTimers();
  });
</script>

{#if total > 0}
  <button
    bind:this={triggerEl}
    type="button"
    class="reaction-summary"
    class:reaction-summary--mobile={interactionMode === 'mobile'}
    aria-label={`Xem ${total} lượt thả cảm xúc`}
    aria-expanded={open}
    title="Xem ai đã thả cảm xúc"
    on:click={openViewer}>
    <span class="reaction-summary__emojis" aria-hidden="true">
      {#each previewReactions as reaction}<span>{reaction}</span>{/each}
    </span>
    <strong>{total}</strong>
  </button>
{/if}

<Portal>
  {#if open}
    <section
      bind:this={popoverEl}
      class="reaction-viewer"
      style={`left:${popupPos.x}px; top:${popupPos.y}px`}
      aria-label="Danh sách người đã thả cảm xúc"
      on:click|stopPropagation
      in:scale={{ duration: 150, start: 0.95, easing: quintOut }}
      out:fade={{ duration: 100 }}>
      {#if effectMode !== 'off'}
        <div class="reaction-viewer__burst" aria-hidden="true">
          {#each burstParticles as particle (particle.id)}
            <span
              class="reaction-viewer__particle"
              class:reaction-viewer__particle--emoji={!!particle.icon}
              style={`--x:${particle.x}%; --y:${particle.y}%; --tx:${particle.tx}px; --ty:${particle.ty}px; --delay:${particle.delay}ms; --size:${particle.size}px; --color:${particle.color};`}>
              {#if particle.icon}
                <span class="reaction-viewer__particle-icon">{particle.icon}</span>
              {/if}
            </span>
          {/each}
        </div>
      {/if}
      <header>
        <div>
          <strong>Cảm xúc</strong>
          <span>{details?.total ?? total} lượt</span>
        </div>
        <button
          type="button"
          class="viewer-settings-toggle"
          aria-label="Cài đặt hiệu ứng"
          aria-expanded={showEffectSettings}
          on:click={() => (showEffectSettings = !showEffectSettings)}>
          <Users size={17} />
        </button>
      </header>

      {#if showEffectSettings}
        <div class="viewer-settings">
          <div class="viewer-settings__group">
            <span>Hiệu ứng</span>
            <button
              type="button"
              class:active={effectMode === 'sequential'}
              on:click={() => setEffectMode('sequential')}>
              Tuần tự
            </button>
            <button type="button" class:active={effectMode === 'random'} on:click={() => setEffectMode('random')}>
              Ngẫu nhiên
            </button>
            <button type="button" class:active={effectMode === 'off'} on:click={() => setEffectMode('off')}>Tắt</button>
          </div>
          <div class="viewer-settings__group">
            <span>Tâm</span>
            <button type="button" class:active={effectOrigin === 'center'} on:click={() => setEffectOrigin('center')}>
              Giữa
            </button>
            <button type="button" class:active={effectOrigin === 'anchor'} on:click={() => setEffectOrigin('anchor')}>
              Theo cạnh
            </button>
          </div>
        </div>
      {/if}

      <div class="reaction-filters" role="tablist" aria-label="Lọc theo cảm xúc">
        <button
          type="button"
          class:active={selectedReaction === 'all'}
          aria-selected={selectedReaction === 'all'}
          on:click={() => (selectedReaction = 'all')}>
          Tất cả <span>{details?.total ?? total}</span>
        </button>
        {#each filters as item (item.reaction)}
          <button
            type="button"
            class:active={selectedReaction === item.reaction}
            aria-selected={selectedReaction === item.reaction}
            on:click={() => (selectedReaction = item.reaction)}>
            {item.reaction}
            <span>{item.count}</span>
          </button>
        {/each}
      </div>

      <div class="reaction-people">
        {#if loading && !details}
          <div class="viewer-state"><LoaderCircle class="animate-spin" size={18} /> Đang tải...</div>
        {:else if error}
          <div class="viewer-state viewer-state--error">
            <span>{error}</span>
            <button type="button" on:click={() => loadDetails(true)}><RefreshCw size={14} /> Thử lại</button>
          </div>
        {:else}
          {#each filteredRows as person (person.userId)}
            <div class="person-row">
              <span class="avatar">
                {#if avatarContentUrl(person.avatar)}
                  <img src={avatarContentUrl(person.avatar)} alt="" loading="lazy" decoding="async" />
                {:else}
                  {getInitial(person.name)}
                {/if}
              </span>
              <span class="person-name">
                <strong>{String(person.userId) === String($myId) ? 'Bạn' : person.name}</strong>
                {#if String(person.userId) === String($myId)}<small>{person.name}</small>{/if}
              </span>
              <span class="person-reaction" aria-label={`Cảm xúc ${person.reaction}`}>{person.reaction}</span>
            </div>
          {/each}

          {#if anonymousForFilter > 0}
            <div class="anonymous-row">và {anonymousForFilter} lượt khác</div>
          {:else if filteredRows.length === 0}
            <div class="viewer-state">Chưa có người thả cảm xúc này.</div>
          {/if}
        {/if}
      </div>
    </section>
  {/if}
</Portal>

<style>
  .reaction-summary {
    display: inline-flex;
    align-items: center;
    height: 1.4rem;
    gap: 0.2rem;
    padding: 0 0.42rem 0 0.3rem;
    border: 1px solid rgba(203, 213, 225, 0.9);
    border-radius: 999px;
    color: #334155;
    background: rgba(255, 255, 255, 0.98);
    box-shadow: 0 6px 16px rgba(15, 23, 42, 0.1);
    transition:
      transform 120ms ease,
      border-color 120ms ease,
      background-color 120ms ease;
  }

  .reaction-summary:hover,
  .reaction-summary:focus-visible {
    z-index: 1;
    transform: translateY(-1px);
    border-color: rgba(59, 130, 246, 0.5);
    background: #eff6ff;
  }

  .reaction-summary__emojis {
    display: inline-flex;
    align-items: center;
    letter-spacing: -0.22rem;
    padding-right: 0.18rem;
    font-size: 0.88rem;
    line-height: 1;
  }

  .reaction-summary__emojis span:last-child {
    letter-spacing: 0;
  }

  .reaction-summary strong {
    font-size: 0.68rem;
    line-height: 1;
  }

  .reaction-summary--mobile {
    height: 1.5rem;
  }

  :global(.reaction-viewer) {
    position: fixed;
    z-index: 10001;
    width: min(18.25rem, calc(100vw - 1.25rem));
    overflow: hidden;
    border: 1px solid rgba(203, 213, 225, 0.95);
    border-radius: 0.75rem;
    color: #0f172a;
    background: rgba(255, 255, 255, 0.99);
    box-shadow: 0 18px 42px rgba(15, 23, 42, 0.2);
    backdrop-filter: blur(14px);
    transform-origin: center bottom;
  }

  :global(.reaction-viewer__burst) {
    position: absolute;
    inset: 0;
    overflow: hidden;
    pointer-events: none;
  }

  :global(.reaction-viewer__particle) {
    position: absolute;
    left: var(--x);
    top: var(--y);
    width: var(--size);
    height: var(--size);
    border-radius: 999px;
    background: color-mix(in srgb, var(--color) 24%, transparent);
    box-shadow:
      0 0 0 1px color-mix(in srgb, var(--color) 35%, white),
      0 0 14px color-mix(in srgb, var(--color) 58%, white);
    transform: translate(-50%, -50%) scale(0.25);
    opacity: 0;
    animation: viewer-burst 920ms cubic-bezier(0.16, 1, 0.3, 1) forwards;
    animation-delay: var(--delay);
    will-change: transform, opacity;
  }

  :global(.reaction-viewer__particle--emoji) {
    background: transparent;
    box-shadow: none;
    text-shadow: 0 0 10px rgba(255, 255, 255, 0.85);
  }

  :global(.reaction-viewer__particle-icon) {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100%;
    font-size: calc(var(--size) * 0.92);
    line-height: 1;
  }

  :global(.reaction-viewer header) {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0.72rem 0.8rem 0.6rem;
    border-bottom: 1px solid #e2e8f0;
  }

  :global(.reaction-viewer header div) {
    display: flex;
    align-items: baseline;
    gap: 0.4rem;
  }

  :global(.viewer-settings-toggle) {
    display: grid;
    place-items: center;
    width: 1.5rem;
    height: 1.5rem;
    border-radius: 999px;
    color: #64748b;
    background: #f8fafc;
  }

  :global(.viewer-settings) {
    display: grid;
    gap: 0.45rem;
    padding: 0.5rem 0.6rem 0.35rem;
    border-bottom: 1px solid #e2e8f0;
    background: linear-gradient(180deg, rgba(248, 250, 252, 0.96), rgba(255, 255, 255, 0.98));
  }

  :global(.viewer-settings__group) {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.3rem;
  }

  :global(.viewer-settings__group > span) {
    min-width: 2.3rem;
    color: #64748b;
    font-size: 0.64rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }

  :global(.viewer-settings__group button) {
    padding: 0.24rem 0.48rem;
    border-radius: 999px;
    color: #475569;
    background: #f1f5f9;
    font-size: 0.66rem;
    font-weight: 600;
  }

  :global(.viewer-settings__group button.active) {
    color: #1d4ed8;
    background: #dbeafe;
  }

  :global(.reaction-viewer header strong) {
    font-size: 0.82rem;
  }

  :global(.reaction-viewer header span) {
    color: #64748b;
    font-size: 0.68rem;
  }

  :global(.reaction-filters) {
    display: flex;
    gap: 0.28rem;
    padding: 0.48rem 0.6rem;
    overflow-x: auto;
    border-bottom: 1px solid #e2e8f0;
    scrollbar-width: none;
  }

  :global(.reaction-filters::-webkit-scrollbar) {
    display: none;
  }

  :global(.reaction-filters button) {
    display: inline-flex;
    flex: 0 0 auto;
    align-items: center;
    gap: 0.22rem;
    padding: 0.28rem 0.45rem;
    border-radius: 999px;
    color: #475569;
    background: #f1f5f9;
    font-size: 0.7rem;
  }

  :global(.reaction-filters button.active) {
    color: #1d4ed8;
    background: #dbeafe;
    font-weight: 650;
  }

  :global(.reaction-filters button span) {
    font-size: 0.64rem;
  }

  :global(.reaction-people) {
    max-height: min(18rem, 46vh);
    overflow-y: auto;
    padding: 0.3rem;
  }

  :global(.person-row) {
    display: flex;
    align-items: center;
    gap: 0.58rem;
    min-height: 2.75rem;
    padding: 0.35rem 0.45rem;
    border-radius: 0.55rem;
  }

  :global(.person-row:hover) {
    background: #f8fafc;
  }

  :global(.avatar) {
    display: grid;
    flex: 0 0 auto;
    place-items: center;
    width: 2rem;
    height: 2rem;
    overflow: hidden;
    border-radius: 999px;
    color: #1d4ed8;
    background: #dbeafe;
    font-size: 0.72rem;
    font-weight: 700;
  }

  :global(.avatar img) {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  :global(.person-name) {
    min-width: 0;
    display: grid;
    flex: 1;
  }

  :global(.person-name strong) {
    overflow: hidden;
    font-size: 0.76rem;
    font-weight: 600;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  :global(.person-name small) {
    overflow: hidden;
    color: #64748b;
    font-size: 0.62rem;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  :global(.person-reaction) {
    display: grid;
    flex: 0 0 auto;
    place-items: center;
    width: 1.8rem;
    height: 1.8rem;
    border-radius: 999px;
    background: #f1f5f9;
    font-size: 1rem;
  }

  :global(.anonymous-row) {
    padding: 0.55rem 0.6rem;
    color: #475569;
    font-size: 0.72rem;
    font-weight: 600;
  }

  :global(.viewer-state) {
    min-height: 5.5rem;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.42rem;
    padding: 0.8rem;
    color: #64748b;
    font-size: 0.72rem;
    text-align: center;
  }

  :global(.viewer-state--error) {
    flex-direction: column;
    color: #b91c1c;
  }

  :global(.viewer-state--error button) {
    display: inline-flex;
    align-items: center;
    gap: 0.28rem;
    padding: 0.35rem 0.55rem;
    border-radius: 0.45rem;
    color: #1d4ed8;
    background: #eff6ff;
    font-weight: 600;
  }

  @keyframes viewer-burst {
    0% {
      opacity: 0;
      transform: translate(-50%, -50%) scale(0.25);
    }
    14% {
      opacity: 1;
    }
    55% {
      opacity: 1;
    }
    100% {
      opacity: 0;
      transform: translate(calc(-50% + var(--tx)), calc(-50% + var(--ty))) scale(0.08);
    }
  }
</style>
